import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import PhotoAlbum from 'react-photo-album';
import { useIntl } from 'react-intl';

import FilesTileItem from './TileItem';

import {
  addSelectedFile,
  removeSelectedFile,
} from '../../actions/Files';

import withDragAndDropContext from '../../helpers/withDragAndDropContext';
import useFileSelection from '../../hooks/useFileSelection';

const FilesTile = ({
  files,
  reorderingMode,
  selectedFiles,
  handleMoveSelectedItems,
  disableRowConstraints,
  selectFile,
  unselectFile,
  ...props
}) => {
  const intl = useIntl();
  const { handleFileSelect } = useFileSelection(files, selectFile, unselectFile);

  const fileRenderer = ({
    photo,
    imageProps,
  }) => {
    const { file } = photo;

    return (
      <FilesTileItem
        id={file.uuid}
        index={file.uuid}
        key={file.uuid}
        file={file}
        handleMoveSelectedItems={handleMoveSelectedItems}
        isSelected={!!selectedFiles.find(sf => (sf.uuid === file.uuid))}
        imageProps={imageProps}
        {...props}
        onFileSelect={
          (fileUuid, isSelected) => handleFileSelect(fileUuid, isSelected)
        }
      />
    );
  };

  const filesToDisplay = useMemo(() => files.map(file => {
    const width = file.attributes.thumbnail_width || file.attributes.width;
    const height = file.attributes.thumbnail_height || file.attributes.height;

    return {
      key: file.uuid,
      width: width || 900,
      height: height || 600,
      file,
    };
  }), [files]);

  if (filesToDisplay.length === 0) {
    return null;
  }

  return (
    <div className="files-masonry files-tile">
      {reorderingMode && (
        <p className="notice">
          {intl.formatMessage({ defaultMessage: 'Switch to grid or list view to reorder files.' })}
        </p>
      )}
      <PhotoAlbum
        photos={filesToDisplay}
        layout="rows"
        renderPhoto={fileRenderer}
        {...(!disableRowConstraints ? { rowConstraints: { singleRowMaxHeight: 400 } } : {})}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  collection: state.Collections.collection,
  selectedFiles: state.Files.selectedFiles,
  reorderingMode: state.Files.reorderingMode,
});

const mapDispatchToProps = dispatch => ({
  selectFile: (file) => {
    dispatch(addSelectedFile(file));
  },
  unselectFile: (fileUuid) => {
    dispatch(removeSelectedFile(fileUuid));
  },
});

FilesTile.propTypes = {
  collection: PropTypes.object,
  selectedFiles: PropTypes.array.isRequired,
  reorderingMode: PropTypes.bool.isRequired,
  files: PropTypes.array.isRequired,
  share: PropTypes.object,
  className: PropTypes.string,
  canDrag: PropTypes.bool,
  handleMoveSelectedItems: PropTypes.func,
  selectFile: PropTypes.func.isRequired,
  unselectFile: PropTypes.func.isRequired,
  displayOnly: PropTypes.bool,
  allowCheck: PropTypes.bool,
  disableRowConstraints: PropTypes.bool,
};

FilesTile.defaultProps = {
  collection: null,
  share: null,
  className: '',
  canDrag: true,
  displayOnly: false,
  allowCheck: true,
  disableRowConstraints: false,
  handleMoveSelectedItems: () => {},
};

export default withDragAndDropContext(connect(mapStateToProps, mapDispatchToProps)(FilesTile));
